import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Milk, Users, ClipboardList, Truck, Receipt, IndianRupee, Sparkles, Check, Store, Newspaper, Droplet, Utensils, Shirt, TrendingUp, ShieldCheck, Clock, MessageCircle } from 'lucide-react';
import Navbar from '../../components/website/Navbar';
import Footer from '../../components/website/Footer';

const Section = ({ children, className = '' }) => (
  <section className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${className}`}>{children}</section>
);

const Chip = ({ children }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-rozops-accent-light text-rozops-accent text-xs font-semibold px-3 py-1">{children}</span>
);

const SolutionCard = ({ icon: Icon, title, desc, accent = 'blue' }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft hover:shadow-medium transition-all p-6 hover:-translate-y-0.5">
    <div className={`w-11 h-11 rounded-xl grid place-items-center mb-4 ${accent === 'green' ? 'bg-rozops-green-light text-rozops-green' : accent === 'purple' ? 'bg-rozops-accent-light text-rozops-accent' : 'bg-rozops-blue-light text-rozops-blue'}`}>
      <Icon className="w-5 h-5" strokeWidth={2.2} />
    </div>
    <div className="font-heading font-bold text-rozops-navy text-lg mb-1.5">{title}</div>
    <div className="text-sm text-rozops-text-muted leading-relaxed">{desc}</div>
  </div>
);

const IndustryCard = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center justify-center gap-2 p-5 rounded-2xl bg-white border border-gray-100 shadow-soft hover:border-rozops-blue/40 transition-all">
    <div className="w-12 h-12 rounded-xl bg-rozops-blue-light text-rozops-blue grid place-items-center">
      <Icon className="w-6 h-6" strokeWidth={2} />
    </div>
    <div className="text-sm font-semibold text-rozops-text">{label}</div>
  </div>
);

const Metric = ({ value, label, accent }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-6">
    <div className={`font-heading font-extrabold text-4xl tracking-tight ${accent === 'green' ? 'text-rozops-green' : accent === 'purple' ? 'text-rozops-accent' : 'text-rozops-blue'}`}>{value}</div>
    <div className="text-sm text-rozops-text-muted mt-1">{label}</div>
  </div>
);

export default function Home() {
  return (
    <div className="bg-rozops-bg min-h-screen">
      <Navbar />

      {/* HERO */}
      <div className="relative overflow-hidden bg-white">
        <Section className="pt-6 pb-12 sm:pt-8 sm:pb-16">
          <div className="animate-fade-in-up">
            <img
              src="/hero-image.png"
              alt="RozOps AI-powered operations software for milk, water can, kirana, and everyday businesses"
              className="w-full h-auto rounded-2xl shadow-medium"
              loading="eager"
              fetchPriority="high"
            />
          </div>
        </Section>
      </div>

      {/* TRUST / INDUSTRIES */}
      <Section className="pb-16">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest font-bold text-rozops-text-muted">Built for businesses that work every day</div>
        </div>
        <div className="grid grid-cols-3 md:grid-cols-6 gap-3 sm:gap-4">
          <IndustryCard icon={Milk} label="Milk Vendors" />
          <IndustryCard icon={Store} label="Kirana Stores" />
          <IndustryCard icon={Droplet} label="Water Suppliers" />
          <IndustryCard icon={Newspaper} label="Newspapers" />
          <IndustryCard icon={Utensils} label="Tiffin Services" />
          <IndustryCard icon={Shirt} label="Laundry" />
        </div>
      </Section>

      {/* PROBLEM */}
      <Section className="py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Chip>The daily reality</Chip>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-rozops-navy tracking-tight">
              Still running your business through notebooks, calls and WhatsApp?
            </h2>
            <ul className="mt-6 space-y-3 text-rozops-text-muted">
              {[
                'Missed quantity changes — customer said 1 packet, you delivered 2',
                'Bills take 3–4 hours every month, and still have mistakes',
                'Payments due for weeks — you keep forgetting who paid',
                'Vacation requests get lost in a hundred WhatsApp messages',
                'No idea what stock or capacity to prepare for tomorrow',
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-red-100 text-red-500 grid place-items-center flex-shrink-0 mt-0.5 text-xs">✕</div>
                  <span className="text-[15px]">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Metric value="3–4 hrs" label="Monthly billing time" />
            <Metric value="₹8,000" label="Avg. wastage per month" accent="green" />
            <Metric value="12%" label="Uncollected payments" />
            <Metric value="20+" label="WhatsApp msgs / day" accent="green" />
          </div>
        </div>
      </Section>

      {/* SOLUTION */}
      <Section className="py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <Chip>The RozOps way</Chip>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-rozops-navy tracking-tight">
            Everything your daily business needs, in one clean place
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          <SolutionCard icon={Users} title="Manage customers" desc="Add customers with address, area and delivery notes. See ledger, subscriptions, dues in one view." />
          <SolutionCard icon={ClipboardList} title="Track subscriptions" desc="Set daily quantity, weekdays and start/end. Pause, resume, change with one tap." accent="green" />
          <SolutionCard icon={Truck} title="Plan daily deliveries" desc="Auto-generated route sheets. Mark entire route delivered in seconds — edit only exceptions." />
          <SolutionCard icon={Receipt} title="Bills, automatically" desc="Monthly invoices generated from actual deliveries. Share on WhatsApp or download PDF." />
          <SolutionCard icon={IndianRupee} title="Collect faster" desc="Track cash, UPI, bank. See pending payments at a glance. Send reminders." accent="green" />
          <SolutionCard icon={TrendingUp} title="Understand your business" desc="Revenue, wastage, collection speed — all in numbers that make sense to you." accent="purple" />
        </div>
      </Section>

      {/* AI ASSISTANT STRIP */}
      <Section className="py-16">
        <div className="rounded-3xl bg-gradient-to-br from-rozops-navy via-rozops-navy to-[#1e3a68] p-8 sm:p-12 text-white relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-80 h-80 rounded-full bg-rozops-accent/30 blur-3xl" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 text-white text-xs font-semibold px-3 py-1"><Sparkles className="w-3.5 h-3.5" /> AI Assistant</div>
              <h3 className="mt-3 font-heading font-bold text-3xl sm:text-4xl tracking-tight">Just tell RozOps what happened. It handles the rest.</h3>
              <p className="mt-4 text-white/70 max-w-xl">Type or speak in plain language. "No delivery for Rakesh tomorrow." "What should I prepare for tomorrow?" "Who hasn't paid this month?"</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {['No delivery tomorrow', 'Pause for vacation 15-20 July', 'Who hasn\'t paid?', 'Prepare list for tomorrow'].map(t => (
                  <span key={t} className="rounded-full bg-white/10 border border-white/10 px-3 py-1.5 text-sm">{t}</span>
                ))}
              </div>
            </div>
            <div className="bg-white/95 rounded-2xl p-5 text-rozops-text shadow-medium">
              <div className="flex items-center gap-2 text-xs text-rozops-text-muted mb-3"><MessageCircle className="w-4 h-4 text-rozops-accent" /> Merchant asked</div>
              <div className="text-lg font-semibold text-rozops-navy">"What should I prepare tomorrow?"</div>
              <div className="mt-3 h-px bg-gray-100" />
              <div className="mt-3 text-sm text-rozops-text-muted">Tomorrow's plan</div>
              <div className="mt-2 flex justify-between items-baseline">
                <div className="font-heading font-bold text-rozops-navy text-3xl">142 units</div>
                <div className="text-rozops-green font-semibold text-sm inline-flex items-center gap-1"><ShieldCheck className="w-4 h-4" /> Excludes 6 vacations</div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section className="py-16">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <Chip>How it works</Chip>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-rozops-navy tracking-tight">Get started in 4 simple steps</h2>
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          {[
            ['Add customers', 'Import or type your customer list.'],
            ['Set subscriptions', 'Daily quantity, weekdays, delivery time.'],
            ['Deliver & mark', 'One tap to complete a whole route.'],
            ['Bill & collect', 'Auto invoices, one-tap payment tracking.'],
          ].map(([t, d], i) => (
            <div key={t} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-soft">
              <div className="w-9 h-9 rounded-lg bg-rozops-blue text-white grid place-items-center font-heading font-bold">{i + 1}</div>
              <div className="mt-4 font-heading font-bold text-rozops-navy">{t}</div>
              <div className="text-sm text-rozops-text-muted mt-1">{d}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* MONEY & GROWTH */}
      <Section className="py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <img src="https://images.pexels.com/photos/16890393/pexels-photo-16890393.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Kirana store" className="rounded-3xl shadow-medium aspect-[4/3] object-cover" />
          <div>
            <Chip><TrendingUp className="w-3.5 h-3.5" /> More money, less loss</Chip>
            <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl text-rozops-navy tracking-tight">Help your business earn more and lose less</h2>
            <div className="mt-6 grid grid-cols-2 gap-4">
              <Metric value="+35%" label="Faster payment collection" accent="green" />
              <Metric value="−22%" label="Wastage reduction" accent="green" />
              <Metric value="3 hrs" label="Billing time / month" />
              <Metric value="0" label="Missed change requests" accent="purple" />
            </div>
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-16">
        <div className="rounded-3xl bg-white border border-gray-100 shadow-medium p-8 sm:p-12 text-center">
          <h3 className="font-heading font-bold text-3xl sm:text-4xl text-rozops-navy tracking-tight">Ready to reduce work and grow your business?</h3>
          <p className="mt-3 text-rozops-text-muted max-w-xl mx-auto">Set up in 15 minutes. Free for up to 25 customers. No credit card required.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/register" data-testid="footer-cta-primary" className="bg-rozops-blue hover:bg-rozops-blue-dark text-white rounded-xl px-6 py-3.5 font-semibold shadow-soft inline-flex items-center gap-2">Start Free <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/contact" data-testid="footer-cta-secondary" className="bg-white border-2 border-rozops-blue-light hover:border-rozops-blue text-rozops-text rounded-xl px-6 py-3.5 font-semibold">Talk to our team</Link>
          </div>
        </div>
      </Section>

      <Footer />
    </div>
  );
}
